import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Button, 
  Grid, 
  Grow,
  TextField,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  CheckCircle, 
  Error, 
  HowToReg, 
  Business, 
  Email as EmailIcon,
  Person,
  AccessTime 
} from '@mui/icons-material';
import API from 'api';
import { toast } from 'react-toastify';
import styles from './index.module.scss';
import SignInImg from 'Assets/Images/login.png';

const ConfirmReference = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('validating'); // 'validating' | 'pending' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [referenceData, setReferenceData] = useState(null);
  const [showTestimonial, setShowTestimonial] = useState(false);
  const [testimonial, setTestimonial] = useState('');
  const [loading, setLoading] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  // Validate token and get reference details on mount
  useEffect(() => {
    const validateAndGetReference = async () => {
      try {
        const token = searchParams.get('token');

        if (!token) {
          toast.error('Invalid reference confirmation link. Please check your email.');
          navigate('/');
          return;
        }

        console.log('Validating reference token:', token.substring(0, 20) + '...');

        // Call API to validate token and get reference details (GET with query param)
        const response = await API.references.validateToken(token);

        if (response.data.success && response.data.reference) {
          setStatus('pending');
          setReferenceData(response.data.reference);
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Invalid or expired reference link.');
        }

      } catch (error) {
        console.error('Reference validation error:', error);
        
        setStatus('error');
        const code = error.response?.data?.code || '';
        setErrorCode(code);
        
        const errorMessage = error.response?.data?.message;
        
        if (code === 'EXPIRED') {
          setMessage('This reference confirmation link has expired. Please contact the advisor for a new link.');
        } else if (code === 'ALREADY_CONFIRMED') {
          setMessage('You have already confirmed this reference request. Thank you!');
        } else if (code === 'ALREADY_DECLINED') {
          setMessage('You have already declined this reference request.');
        } else if (code === 'INVALID_TOKEN') {
          setMessage('Invalid reference confirmation link. Please check your email or contact the advisor.');
        } else {
          setMessage(errorMessage || 'Unable to validate reference link. Please try again.');
        }
      }
    };

    validateAndGetReference();
  }, [searchParams, navigate]);

  // Handle confirm with testimonial
  const handleConfirm = async () => {
    if (showTestimonial) {
      // Submit confirmation with testimonial
      await submitConfirmation();
    } else {
      // Show testimonial input
      setShowTestimonial(true);
    }
  };

  // Submit confirmation
  const submitConfirmation = async () => {
    const token = searchParams.get('token');
    setLoading(true);

    try {
      const response = await API.references.confirm({
        token: token,
        action: 'confirm',
        testimonial: testimonial.trim() || null
      });

      if (response.data.success) {
        setStatus('success');
        setMessage('Thank you for confirming this professional reference! The advisor has been notified.');
        toast.success('Reference confirmed successfully!');
      } else {
        toast.error(response.data.message || 'Failed to confirm reference');
      }
    } catch (error) {
      console.error('Confirmation error:', error);
      toast.error(error.response?.data?.message || 'Failed to confirm reference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle decline - open modal
  const handleDeclineClick = () => {
    setDeclineModalOpen(true);
  };

  // Confirm decline
  const handleDeclineConfirm = async () => {
    const token = searchParams.get('token');
    setDeclineModalOpen(false);
    setLoading(true);

    try {
      const response = await API.references.confirm({
        token: token,
        action: 'decline'
      });

      if (response.data.success) {
        setStatus('success');
        setMessage('You have declined this reference request. The advisor has been notified.');
        toast.info('Reference declined');
      } else {
        toast.error(response.data.message || 'Failed to decline reference');
      }
    } catch (error) {
      console.error('Decline error:', error);
      toast.error(error.response?.data?.message || 'Failed to decline reference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to right, #041e42, #78a8e6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Grid container spacing={0}>
        {/* Left Side - Info */}
        <Grow in timeout={1000}>
          <Grid item xs={12} md={5} sx={{ color: 'white', p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              PROFESSIONAL REFERENCE
            </Typography>
            <Typography variant="h5" gutterBottom>
              Trusted Advisory Service
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.8 }}>
              Your professional reference helps advisors build trust and credibility with their clients.
              Your feedback is valuable and appreciated.
            </Typography>
            <Box component="img" src={SignInImg} alt="illustration" sx={{ width: '60%', maxWidth: 400, mt: 4 }} />
          </Grid>
        </Grow>

        {/* Right Side - Reference Confirmation */}
        <Grid item xs={12} md={7}>
          <Grow in timeout={1000}>
            <Card sx={{ height: '100%', width: "90%", m: "auto", borderRadius: 4, p: 3 }}>
              <CardContent>
                
                {/* Validating State */}
                {status === 'validating' && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.iconContainer}>
                      <CircularProgress size={80} thickness={4} />
                      <HowToReg className={styles.referenceIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.title} sx={{ mt: 3, mb: 2 }}>
                      Validating reference request...
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className={styles.subtitle}>
                      Please wait while we verify your confirmation link
                    </Typography>
                    <Box className={styles.dotsContainer}>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                    </Box>
                  </Box>
                )}

                {/* Pending - Show Reference Details & Actions */}
                {status === 'pending' && referenceData && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.headerIconContainer}>
                      <HowToReg sx={{ fontSize: 50, color: '#1976d2' }} />
                    </Box>
                    
                    <Typography variant="h4" className={styles.title} sx={{ mt: 2, mb: 1 }}>
                      Professional Reference Request
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                      <strong>{referenceData.advisorName}</strong> has requested your professional reference
                    </Typography>

                    <Divider sx={{ width: '100%', mb: 3 }} />

                    {/* Reference Details */}
                    <Box className={styles.detailsBox}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Person sx={{ color: '#1976d2' }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Advisor Name
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {referenceData.advisorName}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        {referenceData.advisorEmail && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <EmailIcon sx={{ color: '#1976d2' }} />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Advisor Email
                                </Typography>
                                <Typography variant="body1">
                                  {referenceData.advisorEmail}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {referenceData.advisorCompany && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Business sx={{ color: '#1976d2' }} />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Company
                                </Typography>
                                <Typography variant="body1">
                                  {referenceData.advisorCompany}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                          <Chip 
                            label={`Relationship: ${referenceData.relationshipType}`}
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        </Grid>

                        {referenceData.yearsKnown && (
                          <Grid item xs={12} sm={6}>
                            <Chip 
                              label={`${referenceData.yearsKnown} years known`}
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                        )}

                        {referenceData.expiresAt && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                This request expires on {new Date(referenceData.expiresAt).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    {/* Testimonial Input (shown after clicking Confirm) */}
                    {showTestimonial && (
                      <Box sx={{ width: '100%', mt: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
                          Add Your Testimonial (Optional)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left' }}>
                          Share your experience working with {referenceData.advisorName}. This will be visible on their profile.
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          variant="outlined"
                          placeholder="Write your testimonial here... (optional, max 2000 characters)"
                          value={testimonial}
                          onChange={(e) => {
                            if (e.target.value.length <= 2000) {
                              setTestimonial(e.target.value);
                            }
                          }}
                          helperText={`${testimonial.length}/2000 characters`}
                          sx={{ mb: 2 }}
                        />
                      </Box>
                    )}

                    {/* Action Buttons */}
                    {!showTestimonial ? (
                      <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 3 }}>
                        <Button
                          variant="contained"
                          className={styles.confirmButton}
                          onClick={handleConfirm}
                          fullWidth
                          disabled={loading}
                          startIcon={<CheckCircle />}
                        >
                          Confirm Reference
                        </Button>
                        <Button
                          variant="outlined"
                          className={styles.declineButton}
                          onClick={handleDeclineClick}
                          fullWidth
                          disabled={loading}
                          color="error"
                        >
                          Decline
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        className={styles.confirmButton}
                        onClick={submitConfirmation}
                        fullWidth
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                        sx={{ mt: 2 }}
                      >
                        {loading ? 'Submitting...' : 'Submit Confirmation'}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Success State */}
                {status === 'success' && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.successIconContainer}>
                      <CheckCircle className={styles.successIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.successTitle} sx={{ mt: 2, mb: 2 }}>
                      Thank You!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className={styles.message} sx={{ mb: 3, textAlign: 'center' }}>
                      {message}
                    </Typography>
                    <Button
                      variant="contained"
                      className={styles.successButton}
                      onClick={() => navigate('/')}
                      fullWidth
                      sx={{ mt: 3 }}
                    >
                      Return to Home
                    </Button>
                  </Box>
                )}

                {/* Error State */}
                {status === 'error' && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.errorIconContainer}>
                      <Error className={styles.errorIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.errorTitle} sx={{ mt: 2, mb: 2 }}>
                      Unable to Process Request
                    </Typography>
                    <Box className={styles.errorBox}>
                      <Typography variant="body1" className={styles.errorMessage}>
                        {message}
                      </Typography>
                    </Box>
                    
                    {/* Show appropriate action button based on error code */}
                    <Button
                      variant="contained"
                      className={styles.actionButton}
                      onClick={() => navigate('/')}
                      fullWidth
                      sx={{ mt: 3 }}
                    >
                      Return to Home
                    </Button>
                  </Box>
                )}
                
              </CardContent>
            </Card>
          </Grow>
        </Grid>
      </Grid>

      {/* Decline Confirmation Modal */}
      <Dialog
        open={declineModalOpen}
        onClose={() => setDeclineModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f' }}>
          Decline Reference Request?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to decline this reference request from <strong>{referenceData?.advisorName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The advisor will be notified of your decision. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeclineModalOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeclineConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Yes, Decline
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfirmReference;
